import {
    BrowserAuthError,
    EventMessage,
    EventType,
    PublicClientApplication,
    type AccountInfo,
    type AuthenticationResult,
} from '@azure/msal-browser';

// TODO: source from runtime config instead of hardcoding.
const CLIENT_ID = 'c4f4dcad-70f4-471b-8a87-463c5eabd09b';
const TENANT_ID = '72f988bf-86f1-41af-91ab-2d7cd011db47';

export const API_SCOPES: readonly string[] = [`api://${CLIENT_ID}/access_as_user`];

const CACHE_RECOVERY_FLAG = 'msal.cacheRecoveryAttempted';
const PENDING_AUTH_NAVIGATION_KEY = 'msal.pendingNavigation';
const PENDING_AUTH_NAVIGATION_MAX_AGE_MS = 15 * 60 * 1000;

interface PendingAuthNavigation {
    relativeUrl: string;
    createdAt: number;
}

export class AuthenticationRedirectInProgressError extends Error {
    constructor(message = 'Authentication redirect is in progress') {
        super(message);
        this.name = 'AuthenticationRedirectInProgressError';
    }
}

export function isAuthenticationRedirectInProgress(error: unknown): error is AuthenticationRedirectInProgressError {
    return error instanceof AuthenticationRedirectInProgressError;
}

function getCurrentRelativeUrl(): string {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function rememberCurrentNavigation(): string {
    const relativeUrl = getCurrentRelativeUrl();
    const pendingNavigation: PendingAuthNavigation = {
        relativeUrl,
        createdAt: Date.now(),
    };

    try {
        window.sessionStorage.setItem(PENDING_AUTH_NAVIGATION_KEY, JSON.stringify(pendingNavigation));
    } catch (error) {
        console.error('Unable to preserve the requested URL for authentication', error);
    }

    return window.location.href;
}

export function restorePendingAuthNavigation(): void {
    let serializedNavigation: string | null = null;
    try {
        serializedNavigation = window.sessionStorage.getItem(PENDING_AUTH_NAVIGATION_KEY);
        window.sessionStorage.removeItem(PENDING_AUTH_NAVIGATION_KEY);
    } catch (error) {
        console.error('Unable to restore the requested URL after authentication', error);
        return;
    }

    if (!serializedNavigation) {
        return;
    }

    try {
        const pendingNavigation = JSON.parse(serializedNavigation) as Partial<PendingAuthNavigation>;
        const isFresh = typeof pendingNavigation.createdAt === 'number'
            && Date.now() - pendingNavigation.createdAt <= PENDING_AUTH_NAVIGATION_MAX_AGE_MS;
        const isSafeRelativeUrl = typeof pendingNavigation.relativeUrl === 'string'
            && pendingNavigation.relativeUrl.startsWith('/')
            && !pendingNavigation.relativeUrl.startsWith('//');

        if (isFresh && isSafeRelativeUrl && pendingNavigation.relativeUrl !== getCurrentRelativeUrl()) {
            window.history.replaceState(window.history.state, '', pendingNavigation.relativeUrl);
        }
    } catch (error) {
        console.error('Unable to parse the preserved authentication URL', error);
    }
}

// BrowserAuthError codes that indicate stale cache from the v2 -> v5 upgrade rather than a
// genuine auth failure; only these should trigger a cache purge.
const RECOVERABLE_AUTH_ERROR_CODES = new Set(['timed_out', 'interaction_in_progress']);

export function isRecoverableAuthError(error: unknown): boolean {
    return error instanceof BrowserAuthError && RECOVERABLE_AUTH_ERROR_CODES.has(error.errorCode);
}

export function recoverFromStaleCache(): boolean {
    try {
        if (window.sessionStorage.getItem(CACHE_RECOVERY_FLAG)) {
            return false;
        }
        const isMsalKey = (key: string): boolean => key.startsWith('msal.') || key.includes(CLIENT_ID);
        for (const key of Object.keys(window.localStorage)) {
            if (isMsalKey(key)) {
                window.localStorage.removeItem(key);
            }
        }
        for (const key of Object.keys(window.sessionStorage)) {
            if (key !== CACHE_RECOVERY_FLAG && isMsalKey(key)) {
                window.sessionStorage.removeItem(key);
            }
        }
        // Mark as attempted only after cleanup succeeds so a storage failure doesn't trap the user.
        window.sessionStorage.setItem(CACHE_RECOVERY_FLAG, '1');
    } catch (error) {
        console.error('MSAL cache recovery failed', error);
        return false;
    }
    window.location.reload();
    return true;
}

export const msalInstance = new PublicClientApplication({
    auth: {
        clientId: CLIENT_ID,
        authority: `https://login.microsoftonline.com/${TENANT_ID}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
    },
    cache: {
        cacheLocation: 'localStorage',
    },
});

export async function startLoginRedirect(): Promise<void> {
    await msalInstance.loginRedirect({
        scopes: [...API_SCOPES],
        redirectStartPage: rememberCurrentNavigation(),
    });
}

export async function startTokenRedirect(account?: AccountInfo): Promise<void> {
    await msalInstance.acquireTokenRedirect({
        scopes: [...API_SCOPES],
        account,
        redirectStartPage: rememberCurrentNavigation(),
    });
}

// Must be called once at app startup before React renders.
export async function initMsal(): Promise<void> {
    let redirectResult: AuthenticationResult | null = null;
    try {
        // MSAL v3+ requires explicit initialization before any other API call.
        await msalInstance.initialize();
        redirectResult = await msalInstance.handleRedirectPromise();
    } catch (error) {
        if (isRecoverableAuthError(error) && recoverFromStaleCache()) {
            throw new AuthenticationRedirectInProgressError('Recovering from stale authentication state');
        }
        console.error('MSAL initialization failed', error);
        throw error;
    }

    if (redirectResult?.account) {
        msalInstance.setActiveAccount(redirectResult.account);
    } else if (!msalInstance.getActiveAccount()) {
        const [firstAccount] = msalInstance.getAllAccounts();
        if (firstAccount) {
            msalInstance.setActiveAccount(firstAccount);
        }
    }

    if (msalInstance.getActiveAccount()) {
        restorePendingAuthNavigation();
    }

    msalInstance.addEventCallback((event: EventMessage) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const account = (event.payload as AuthenticationResult).account as AccountInfo | undefined;
            if (account) {
                msalInstance.setActiveAccount(account);
            }
        }
    });
}
